import { Blueprint } from '../../models/blueprint.model';
import { BlueprintItem } from '../../models/blueprint-item.model';
import {
  CustomQuoteTree,
  CustomQuoteTreeNode,
  customQuoteTreeHasDisplayableRoot,
  flattenCustomQuoteTreeForDisplay,
} from '../../models/drawing-file.model';
import {
  QuantityType,
  QuoteItemData,
} from '../../models/quote-item-data.model';

export type CustomTreeProposalSummary = {
  rootName: string;
  nodeCount: number;
  structureSummary: string;
  reason: string;
  confidence: number;
};

export type BlueprintUnitCostDefaultsOptions = {
  /** Convert blueprint base currency unit_cost to display currency. */
  convertUnitCost?: (unitCost: string) => string;
  modelOutputsUnitCost?: (pricingModelId: number) => boolean;
};

type BlueprintRateCandidate = {
  name: string;
  unitCost: number;
  unitCostRaw: string;
  markupPercentage: string | null;
};

/**
 * A mix of zero and positive rates for the same set is ambiguous and must not reuse a rate
 * (zeros are not silently discarded). Reuse only when every candidate
 * shares one strictly positive unit_cost.
 */
export function consensusBlueprintUnitCost(
  candidates: BlueprintRateCandidate[],
): { unitCostRaw: string; markupPercentage?: string } | null {
  if (!candidates.length) {
    return null;
  }

  const distinctCosts = new Set(
    candidates.map((candidate) => candidate.unitCost),
  );
  if (distinctCosts.size !== 1) {
    return null;
  }

  const [unitCost] = distinctCosts;
  if (!(unitCost > 0)) {
    return null;
  }

  const unitCostRaw = candidates[0].unitCostRaw;
  const markups = new Set(
    candidates.map((candidate) => candidate.markupPercentage ?? ''),
  );
  if (markups.size === 1) {
    const [markup] = markups;
    if (markup !== '') {
      return { unitCostRaw, markupPercentage: markup };
    }
  }
  return { unitCostRaw };
}

/**
 * Convert a resolved AI custom-tree root into flat QuoteItemData rows.
 * Catalogue ids are taken only when they are finite numbers. Unmatched
 * material and pricing-model names are kept as selection hints. A child
 * with no material inherits from an ancestor that already has a catalogue
 * material.
 */
export function customTreeToQuoteItemData(
  root: CustomQuoteTreeNode,
  options?: {
    createPersistentId?: () => string;
  },
): QuoteItemData[] {
  const createPersistentId =
    options?.createPersistentId ?? createLocalPersistentId;
  const items: QuoteItemData[] = [];

  const walk = (
    node: CustomQuoteTreeNode,
    parentId: string | null,
    siblingOrder: number,
    ancestorProvidesMaterial: boolean,
  ): void => {
    const persistentId = createPersistentId();
    const materialId = toCatalogueNumberId(node.material_id);
    const pricingModelId = toCatalogueNumberId(node.pricing_model_id);
    const quantity = sanitizeQuantity(node.quantity);
    const inheritMaterial =
      parentId != null &&
      materialId == null &&
      !hasOwnMaterialName(node) &&
      ancestorProvidesMaterial;

    const unmatchedMaterialName =
      materialId == null ? unmatchedCatalogueName(node.material_name) : null;
    const unmatchedPricingModelName =
      pricingModelId == null
        ? unmatchedCatalogueName(node.pricing_model_name)
        : null;

    const item: QuoteItemData = {
      persistent_id: persistentId,
      name: node.name.trim(),
      quantity: String(quantity),
      quantity_type: QuantityType.UNIT,
      parent_id: parentId,
      sibling_order: siblingOrder,
      cad_object_ids: [],
      material_id: materialId,
      ...(inheritMaterial ? { inherit_material: true } : {}),
      ...(pricingModelId != null ? { pricing_model_id: pricingModelId } : {}),
      ...(unmatchedMaterialName
        ? { suggested_material_name: unmatchedMaterialName }
        : {}),
      ...(unmatchedPricingModelName
        ? { suggested_pricing_model_name: unmatchedPricingModelName }
        : {}),
    };
    items.push(item);

    const providesMaterialForChildren = materialId != null || inheritMaterial;
    const children = Array.isArray(node.children) ? node.children : [];
    children.forEach((child, index) => {
      if (!child || typeof child.name !== 'string' || !child.name.trim()) {
        return;
      }
      walk(child, persistentId, index, providesMaterialForChildren);
    });
  };

  if (!root || !root.name.trim()) {
    return [];
  }
  walk(root, null, 0, false);
  return items;
}

/**
 * For eligible AI custom-tree leaves, reuse unit_cost (and markup when
 * agreed) from tenant blueprint items when matching evidence is
 * unambiguous and positive.
 *
 * Matching:
 * 1. Candidates = all loaded blueprint items with the same pricing_model_id
 *    (zeros included, any conflicting zero+positive evidence blocks reuse).
 * 2. If that set has no single positive consensus, narrow by exact
 *    case-insensitive name and re-evaluate the same consensus rule.
 * 3. Skip parents, items without a pricing model, and models that output
 *    unit_cost themselves.
 */
export function applyBlueprintUnitCostDefaults(
  items: QuoteItemData[],
  blueprints: Blueprint[],
  options?: BlueprintUnitCostDefaultsOptions,
): QuoteItemData[] {
  if (!items.length || !blueprints.length) {
    return items;
  }

  const convertUnitCost =
    options?.convertUnitCost ?? ((value: string) => value);
  const modelOutputsUnitCost = options?.modelOutputsUnitCost;
  const parentIds = new Set(
    items
      .map((item) => item.parent_id)
      .filter((parentId): parentId is string => parentId != null),
  );
  const rateCandidates = flattenBlueprintRateCandidates(blueprints);

  return items.map((item) => {
    if (parentIds.has(item.persistent_id)) {
      return item;
    }
    const pricingModelId = item.pricing_model_id;
    if (pricingModelId == null || !Number.isFinite(pricingModelId)) {
      return item;
    }
    if (modelOutputsUnitCost?.(pricingModelId)) {
      return item;
    }

    const byModel = rateCandidates.filter(
      (candidate) => candidate.pricingModelId === pricingModelId,
    );
    let consensus = consensusBlueprintUnitCost(byModel);
    if (!consensus) {
      const normalizedName = normalizeItemName(item.name);
      const byName = byModel.filter(
        (candidate) => normalizeItemName(candidate.name) === normalizedName,
      );
      consensus = consensusBlueprintUnitCost(byName);
    }
    if (!consensus) {
      return item;
    }

    const patched: QuoteItemData = {
      ...item,
      unit_cost: convertUnitCost(consensus.unitCostRaw),
    };
    if (consensus.markupPercentage != null) {
      patched.markup_percentage = consensus.markupPercentage;
    }
    return patched;
  });
}

/** True when a leaf needs a user-entered hourly rate. */
export function isMissingHourlyRate(
  unitCost: string | number | null | undefined,
): boolean {
  if (unitCost === null || unitCost === undefined || unitCost === '') {
    return true;
  }
  const parsed = typeof unitCost === 'number' ? unitCost : Number(unitCost);
  return Number.isFinite(parsed) && parsed === 0;
}

function flattenBlueprintRateCandidates(
  blueprints: Blueprint[],
): Array<BlueprintRateCandidate & { pricingModelId: number }> {
  const candidates: Array<BlueprintRateCandidate & { pricingModelId: number }> =
    [];
  for (const blueprint of blueprints) {
    for (const item of blueprint.items ?? []) {
      collectBlueprintRateCandidate(item, candidates);
    }
  }
  return candidates;
}

function collectBlueprintRateCandidate(
  item: BlueprintItem,
  candidates: Array<BlueprintRateCandidate & { pricingModelId: number }>,
): void {
  const pricingModelId = item.pricing_model_id;
  if (pricingModelId != null && Number.isFinite(pricingModelId)) {
    const unitCostRaw =
      item.unit_cost === null || item.unit_cost === undefined
        ? '0'
        : String(item.unit_cost);
    const parsed = Number(unitCostRaw);
    candidates.push({
      pricingModelId,
      name: item.name ?? '',
      unitCost: Number.isFinite(parsed) ? parsed : 0,
      unitCostRaw: Number.isFinite(parsed) ? unitCostRaw : '0',
      markupPercentage:
        item.markup_percentage === null || item.markup_percentage === undefined
          ? null
          : String(item.markup_percentage),
    });
  }
}

function normalizeItemName(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

/** Compact summary for the blueprint-selection step. */
export function summarizeCustomQuoteTree(
  tree: CustomQuoteTree | null | undefined,
): CustomTreeProposalSummary | null {
  if (!customQuoteTreeHasDisplayableRoot(tree)) {
    return null;
  }
  const rows = flattenCustomQuoteTreeForDisplay(tree!.root);
  const rootName = rows[0]?.name ?? '';
  const topLevelChildren = rows
    .filter((row) => row.depth === 1)
    .map((row) => row.name);
  return {
    rootName,
    nodeCount: rows.length,
    structureSummary: topLevelChildren.length
      ? topLevelChildren.join(', ')
      : 'Part only',
    reason: tree!.reason?.trim() ?? '',
    confidence: tree!.confidence,
  };
}

function sanitizeQuantity(value: unknown): number {
  if (typeof value === 'boolean' || typeof value !== 'number') {
    return 1;
  }
  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 1) {
    return 1;
  }
  return value;
}

function toCatalogueNumberId(
  value: string | number | null | undefined,
): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function hasOwnMaterialName(node: CustomQuoteTreeNode): boolean {
  return unmatchedCatalogueName(node.material_name) != null;
}

function unmatchedCatalogueName(
  value: string | null | undefined,
): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const name = value.trim();
  return name === '' ? null : name;
}

function createLocalPersistentId(): string {
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `local-${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}
